import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";
import { type AuthUser, getCachedUser, getToken } from "@/api/client";
import { loginWithGoogle, getMe, logout as apiLogout } from "@/api/auth";

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  loginGoogle: (id_token: string) => Promise<{ error?: string }>;
  signOut: () => void;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(() => getCachedUser());
  const [loading, setLoading] = useState(true);

  const refreshSession = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const me = await getMe();
      setUser(me);
    } catch {
      setUser(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    refreshSession();
  }, [refreshSession]);

  const loginGoogle = useCallback(async (id_token: string) => {
    try {
      const u = await loginWithGoogle(id_token);
      setUser(u);
      return {};
    } catch (err: any) {
      return { error: err.message || 'Login failed' };
    }
  }, []);

  const signOut = useCallback(() => {
    apiLogout();
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, loginGoogle, signOut, refreshSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
